/**
 * "Request a feature" — the gradient pill in the top tab nav (left of
 * "More ▾") and its one-box modal. Web only.
 *
 * Posts the same endpoint as the feedback form (POST /api/feedback) with
 * reason/source 'feature_request' plus where the merchant was (page +
 * app language); the backend adds the shop's contact e-mail and plan,
 * persists a ticket and e-mails the inbox. Validated-form pattern: too
 * short → red field + focus + toast, never a silent no-op; the draft
 * survives an error (Retry) and a Cancel until it has been sent.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Modal, TextField, Text, BlockStack, InlineStack, Button, Banner } from '@shopify/polaris';
import { apiPost, humanizeError } from '../lib/api';
import { FormErrorToast, SubmitButton, ValidatedField } from './forms/ValidatedForm';
import useValidatedForm from './forms/useValidatedForm';
import { useTranslation } from '../i18n';

export const FEATURE_REQUEST_MIN = 10;
export const FEATURE_REQUEST_MAX = 2000;

export function FeatureRequestModal({ open, onClose, page, language }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState(null);
  const textRef = useRef(null);

  const values = useMemo(() => ({ request: text }), [text]);
  const fields = useMemo(() => [{
    id: 'request',
    label: t('featureRequest.label'),
    required: true,
    errorMessage: t('featureRequest.tooShort'),
    validate: (v) => {
      const len = String(v || '').trim().length;
      if (len < FEATURE_REQUEST_MIN) return t('featureRequest.tooShort');
      if (len > FEATURE_REQUEST_MAX) return t('featureRequest.tooLong');
      return null;
    },
  }], [t]);

  const form = useValidatedForm({
    fields,
    values,
    onSubmit: async () => {
      setError(null);
      try {
        const res = await apiPost('/api/feedback', {
          reason: 'feature_request',
          source: 'feature_request',
          message: text.trim(),
          surface: 'web',
          platform: 'web',
          context: { page: page || null, language: language || null },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const err = new Error(data.error || data.message || `HTTP ${res.status}`);
          err.status = res.status;
          throw err;
        }
        setTicket(data.ticket ?? 0);
      } catch (err) {
        setError(humanizeError(err, t('featureRequest.errorGeneric')));
      }
    },
  });
  const submitting = form.submitting;

  // Autofocus the box every time the modal opens (Polaris mounts the
  // field after its own focus trap settles, so a tick later is reliable).
  useEffect(() => {
    if (!open || ticket != null) return undefined;
    const id = setTimeout(() => { textRef.current?.querySelector('textarea')?.focus(); }, 50);
    return () => clearTimeout(id);
  }, [open, ticket]);

  const close = () => { if (submitting) return; setError(null); onClose(); };
  const done = () => { setTicket(null); setText(''); setError(null); onClose(); };

  return (
    <Modal open={open} onClose={ticket != null ? done : close} title={t('featureRequest.title')}>
      <Modal.Section>
        {ticket != null ? (
          <div data-testid="feature-request-success" role="status">
            <BlockStack gap="300" inlineAlign="start">
              <span aria-hidden="true" style={{ fontSize: 28, lineHeight: 1 }}>💌</span>
              <Text as="h3" variant="headingMd">{t('featureRequest.successTitle')}</Text>
              <Text as="p">{t('featureRequest.successBody')}</Text>
              {ticket ? <Text as="p" variant="bodySm" tone="subdued">{t('featureRequest.reference', { ticket })}</Text> : null}
              <InlineStack gap="300">
                <span data-testid="feature-request-done"><Button variant="primary" onClick={done}>{t('common.done')}</Button></span>
              </InlineStack>
            </BlockStack>
          </div>
        ) : (
          <BlockStack gap="400">
            <Text as="p">{t('featureRequest.intro')}</Text>
            <div ref={textRef} data-testid="feature-request-field">
              <ValidatedField id="request" bare form={form}>
                <TextField
                  label={t('featureRequest.label')}
                  labelHidden
                  value={text}
                  onChange={setText}
                  multiline={5}
                  maxLength={FEATURE_REQUEST_MAX}
                  showCharacterCount
                  autoComplete="off"
                  autoFocus
                  disabled={submitting}
                  placeholder={t('featureRequest.placeholder')}
                  {...form.fieldProps('request')}
                />
              </ValidatedField>
            </div>
            {error && (
              <div data-testid="feature-request-error">
                <Banner tone="critical" onDismiss={() => setError(null)}>
                  <p>{error}</p>
                </Banner>
              </div>
            )}
            <InlineStack gap="300" align="start" blockAlign="center">
              {error ? (
                <span data-testid="feature-request-retry"><SubmitButton form={form} label={t('featureRequest.retry')} /></span>
              ) : (
                <span data-testid="feature-request-send"><SubmitButton form={form} label={t('featureRequest.send')} /></span>
              )}
              <Button onClick={close} disabled={submitting}>{t('common.cancel')}</Button>
            </InlineStack>
            <FormErrorToast form={form} />
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

/**
 * The nav pill + its modal. `page`/`language` default to the current
 * route and app language. Measured by AppLayout (via the wrapper it
 * renders around this) so the visible-tabs logic never squeezes it.
 */
export default function FeatureRequestButton() {
  const [open, setOpen] = useState(false);
  const { t, language } = useTranslation();
  const location = useLocation();
  return (
    <>
      <button
        type="button"
        className="au-feature-btn"
        data-testid="feature-request-button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="au-feature-btn__icon" aria-hidden="true">✨</span>
        {t('featureRequest.button')}
      </button>
      <FeatureRequestModal open={open} onClose={() => setOpen(false)} page={location.pathname} language={language} />
    </>
  );
}
